const integer = (n: unknown, min = 0): n is number => typeof n === "number" && Number.isInteger(n) && n >= min
const finite = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n)
const record = (v: unknown): v is Record<string, any> => !!v && typeof v === "object" && !Array.isArray(v)

/** The image an atlas names, as a published destination beside the atlas. */
export const atlasImageDestination = (atlasDestination: string, image: string): string | undefined => {
    if (typeof image !== "string" || !image || image.includes("\\") || image.startsWith("/")) return undefined
    const parts = atlasDestination.split("/").slice(0, -1)
    for (const part of image.split("/")) {
        if (part === "..") { if (!parts.length) return undefined; parts.pop() }
        else if (part && part !== ".") parts.push(part)
    }
    return parts.join("/")
}

/**
 * TexturePacker hash atlases as the client's Pixi spritesheet loader reads them: every frame is a
 * whole-pixel rectangle inside the named image, and every animation names existing frames.
 */
export const validateAtlas = (atlas: unknown, image: { width: number; height: number }, options: { allowTrimOutsideSource?: boolean } = {}): string[] => {
    const problems: string[] = []
    if (!record(atlas) || !record(atlas.meta)) return ["atlas must be an object with meta"]
    if (!record(atlas.frames) || !Object.keys(atlas.frames).length) return ["atlas has no frames"]
    const size = atlas.meta.size
    if (!record(size) || size.w !== image.width || size.h !== image.height) problems.push(`meta.size ${size?.w}x${size?.h} differs from the image ${image.width}x${image.height}`)
    for (const [name, entry] of Object.entries(atlas.frames)) {
        if (!record(entry) || !record(entry.frame)) { problems.push(`frame ${name} has no rectangle`); continue }
        const { x, y, w, h } = entry.frame
        if (!integer(x) || !integer(y) || !integer(w, 1) || !integer(h, 1)) { problems.push(`frame ${name} is not a whole-pixel rectangle`); continue }
        // A rotated frame occupies its rectangle turned on its side.
        const [width, height] = entry.rotated === true ? [h, w] : [w, h]
        if (x + width > image.width || y + height > image.height) problems.push(`frame ${name} lies outside the ${image.width}x${image.height} image`)
        if (entry.rotated !== undefined && typeof entry.rotated !== "boolean") problems.push(`frame ${name} has an invalid rotation flag`)
        if (entry.anchor !== undefined && (!record(entry.anchor) || !finite(entry.anchor.x) || !finite(entry.anchor.y))) problems.push(`frame ${name} has an invalid anchor`)
        if (entry.sourceSize !== undefined && (!record(entry.sourceSize) || !integer(entry.sourceSize.w, 1) || !integer(entry.sourceSize.h, 1))) problems.push(`frame ${name} has an invalid source size`)
        const trim = entry.spriteSourceSize
        if (trim !== undefined && (!record(trim) || !integer(trim.x) || !integer(trim.y) || !integer(trim.w, 1) || !integer(trim.h, 1))) problems.push(`frame ${name} has an invalid trim rectangle`)
        else if (trim && record(entry.sourceSize) && !options.allowTrimOutsideSource && (trim.x + trim.w > entry.sourceSize.w || trim.y + trim.h > entry.sourceSize.h)) problems.push(`frame ${name} is trimmed outside its source size`)
    }
    if (atlas.animations !== undefined) {
        if (!record(atlas.animations)) problems.push("animations must be an object")
        else for (const [name, frames] of Object.entries(atlas.animations)) {
            if (!Array.isArray(frames) || frames.some(frame => typeof frame !== "string" || !Object.prototype.hasOwnProperty.call(atlas.frames, frame))) problems.push(`animation ${name} names a missing frame`)
        }
    }
    return problems
}

